/* eslint-disable @next/next/no-img-element */
import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'

import { Layout, Seo, PageHero } from '@/components/generals'

type SubmitStatus = 'idle' | 'loading' | 'done' | 'error'

const ContactPage: FC = (): ReactNode => {
    const [status, setStatus] = useState<SubmitStatus>('idle')
    const [error, setError] = useState('')

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()
        const data = new FormData(event.currentTarget)

        setStatus('loading')
        setError('')

        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    firstName: data.get('First-Name'),
                    lastName: data.get('Last-Name'),
                    email: data.get('Email'),
                    message: data.get('Message')
                })
            })

            const json = await res.json()
            if (!res.ok || !json.ok) throw new Error(json.error || 'Something went wrong.')

            setStatus('done')
        } catch (err) {
            setStatus('error')
            setError(err instanceof Error ? err.message : 'Something went wrong.')
        }
    }

    return (
        <Layout>
            <Seo title='Contact' description='Let us know if we can help you. Reach out to the San Diego team.' />

            <PageHero
                badge='New'
                eyebrow='Introducing the Class app'
                eyebrowHref='/features'
                title={
                    <>
                        Let us know if we <span className='text-gradient'>can help</span> you
                    </>
                }
                description='Questions about features, pricing, or your account? Send us a note and our team will get back to you quickly.'
                showButtons
            />

            <div className='section'>
                <div className='container'>
                    <div className='animation-on-page-load'>
                        <div className='contact-grid-holder'>
                            <div className='contact-left-side-holder contact-compact'>
                                <div>
                                    <h2 className='heading-3-style'>Contact</h2>
                                    <p className='contact-intro'>Reach out via email, or use the form below and we&apos;ll reply soon.</p>
                                </div>
                                <div className='form-block w-form'>
                                    {status !== 'done' ? (
                                        <form className='form' onSubmit={onSubmit}>
                                            <div className='contact-form-grid'>
                                                <div>
                                                    <label htmlFor='First-Name' className='field-label'>
                                                        First name *
                                                    </label>
                                                    <input className='text-field w-input' maxLength={256} name='First-Name' type='text' id='First-Name' required />
                                                </div>
                                                <div>
                                                    <label htmlFor='Last-Name' className='field-label'>
                                                        Last name *
                                                    </label>
                                                    <input className='text-field w-input' maxLength={256} name='Last-Name' type='text' id='Last-Name' required />
                                                </div>
                                                <div className='form-field-full'>
                                                    <label htmlFor='Email' className='field-label'>
                                                        Email *
                                                    </label>
                                                    <input className='text-field w-input' maxLength={256} name='Email' type='email' id='Email' required />
                                                </div>
                                                <div className='form-field-full'>
                                                    <label htmlFor='Message' className='field-label'>
                                                        Message
                                                    </label>
                                                    <textarea maxLength={5000} id='Message' name='Message' placeholder='How can we help?' className='text-area w-input' />
                                                </div>
                                            </div>
                                            <div>
                                                <input
                                                    type='submit'
                                                    className='button rounded-orage-button no-margin w-button'
                                                    value={status === 'loading' ? 'Sending…' : 'Submit'}
                                                    disabled={status === 'loading'}
                                                />
                                            </div>
                                            {status === 'error' && <p className='contact-error'>{error}</p>}
                                        </form>
                                    ) : (
                                        <div className='success-message w-form-done' style={{ display: 'block' }}>
                                            <div>Thank you! Your message has been received — we&apos;ll be in touch soon.</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className='contact-right-side-holder'>
                                <img
                                    src='/images/About1.webp'
                                    loading='lazy'
                                    sizes='(max-width: 479px) 29vw, (max-width: 767px) 95vw, (max-width: 1279px) 32vw, 414.671875px'
                                    srcSet='/images/About1-p-500.jpg 500w, /images/About1-p-800.jpg 800w, /images/About1-p-1080.jpg 1080w, /images/About1-p-1600.jpg 1600w, /images/About1.webp 1968w'
                                    alt='San Diego team'
                                    className='contact-image'
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export default ContactPage
